import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PawPrint, X, Home, Sparkles } from "lucide-react";
import {
  shouldShowDiscountModal,
  markDiscountDismissed,
  markDiscountDismissedThisSession,
  getVisitorId,
  hasBooked,
  markHasBooked,
  getSourceAttribution,
} from "@/lib/visitorTracking";
import { trackJourneyEvent } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";

const PROMO_CODE = "WELCOMEBACK";
const EXCLUDED_PREFIXES = ["/booking", "/admin", "/auth", "/thank-you"];

const ReturningVisitorModal = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (EXCLUDED_PREFIXES.some((p) => location.pathname.startsWith(p))) return;
    if (!shouldShowDiscountModal()) return;

    let cancelled = false;
    // Random delay between 15-20 seconds
    const delay = 15000 + Math.floor(Math.random() * 5000);
    const timer = setTimeout(async () => {
      try {
        const { data } = await supabase.functions.invoke("check-visitor-booking-status", {
          body: { visitor_id: getVisitorId() },
        });
        if (data?.hasBooked) {
          markHasBooked();
          return;
        }
      } catch { /* ignore */ }
      if (cancelled || hasBooked()) return;
      setOpen(true);
      trackJourneyEvent("discount_modal_shown", { promo_code: PROMO_CODE });
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [location.pathname]);

  const handleCta = () => {
    const src = getSourceAttribution();
    const params = new URLSearchParams();
    if (src.source_location_slug) params.set("neighborhood", src.source_location_slug);
    params.set("promo", PROMO_CODE);
    trackJourneyEvent("discount_modal_cta_click", { promo_code: PROMO_CODE });
    setOpen(false);
    navigate(`/booking?${params.toString()}`);
  };

  const handleDismiss = () => {
    trackJourneyEvent("discount_modal_dismissed", { promo_code: PROMO_CODE });
    markDiscountDismissed();
    markDiscountDismissedThisSession();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleDismiss(); }}>
      <DialogContent className="sm:max-w-md border-0 p-0 overflow-hidden bg-transparent shadow-none">
        <div className="relative rounded-2xl bg-gradient-to-br from-[#00D7D2] via-[#4dabf7] to-[#FF4DDB] p-[2px]">
          <div className="rounded-2xl bg-background p-8 text-center">
            <button
              onClick={handleDismiss}
              aria-label="Close"
              data-gtm-id="returning-visitor-modal-close-click"
              className="absolute right-4 top-4 rounded-full p-1.5 text-muted-foreground hover:bg-muted transition min-h-6 min-w-6"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#00D7D2] to-[#FF4DDB]">
              <PawPrint className="h-7 w-7 text-white" />
            </div>

            <h2 className="text-2xl font-bold mb-2">Welcome back! 🐾</h2>
            <p className="text-muted-foreground mb-4">
              Get <span className="font-semibold text-foreground">10% off</span> your first booking.
            </p>

            <div className="mb-6 rounded-lg bg-primary/5 border border-primary/20 px-4 py-3 text-sm">
              <div className="flex items-center justify-center gap-2 text-foreground">
                <Sparkles className="h-4 w-4 text-primary" />
                <span>
                  Code <span className="font-mono font-bold">{PROMO_CODE}</span> is applied automatically at checkout.
                </span>
              </div>
            </div>

            <div className="mb-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Home className="h-3.5 w-3.5" />
              <span>Mobile grooming at your home. No driving, no waiting room.</span>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                onClick={handleCta}
                size="lg"
                data-gtm-id="returning-visitor-modal-book-cta-click"
                className="w-full bg-gradient-to-r from-[#00D7D2] to-[#FF4DDB] text-white hover:opacity-90 min-h-11"
              >
                Continue booking — apply 10% off
              </Button>
              <button
                onClick={handleDismiss}
                data-gtm-id="returning-visitor-modal-maybe-later-click"
                className="text-sm text-muted-foreground hover:text-foreground min-h-6"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReturningVisitorModal;
